import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { claimTransferByCodeApi } from '@/api/vendo';

/**
 * Flow B buyer-side entry. The seller has given the buyer an 8-char
 * claim code out-of-band (phone, in-person, Messenger). The buyer types
 * it here; on success, this modal closes and hands off to the Review
 * modal with the resolved transfer.
 *
 * Email match is enforced server-side — a leaked code only "locates"
 * the transfer, it doesn't authorise it. So we keep the input simple
 * and let the server bounce wrong-recipient attempts.
 */

const ClaimDevice: React.FC = () => {
   const { handleClose, handleShow } = useUIStore();
   const [code, setCode] = useState('');
   const [working, setWorking] = useState(false);

   const handleSubmit = async () => {
      if (working) return;
      const normalised = code.replace(/[\s-]/g, '').toUpperCase();
      if (normalised.length !== 8) {
         toast.error('Codes are 8 characters');
         return;
      }
      setWorking(true);
      try {
         const transfer = await claimTransferByCodeApi(normalised);
         handleClose();
         // Hand off to the Review modal a tick later so the previous
         // modal fully unmounts before the new one mounts — otherwise
         // both run animations simultaneously and the click feels janky.
         setTimeout(() => {
            handleShow({
               template: 'review-transfer',
               title: 'Review device transfer',
               data: { transfer },
            });
         }, 50);
      } catch (err: any) {
         const code = err?.response?.data?.code;
         const msg =
            err?.response?.data?.error ||
            (code === 'not-found'
               ? "No pending transfer matches that code"
               : code === 'wrong-recipient'
               ? "That code's for a different account"
               : code === 'expired'
               ? 'That transfer has expired'
               : 'Could not look up the code');
         toast.error(msg);
         setWorking(false);
      }
   };

   // Live formatter: capitalise + auto-insert dash after 4 chars so the
   // input mirrors the seller's display (XXXX-XXXX).
   const onChange = (raw: string) => {
      const clean = raw.replace(/[\s-]/g, '').toUpperCase().slice(0, 8);
      const display =
         clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
      setCode(display);
   };

   return (
      <Wrapper>
         <Intro>
            <Icon>🔑</Icon>
            <Heading>Enter your claim code</Heading>
            <Sub>
               The seller will have read you an 8-character code. Type it below
               — works for buyers who don't have the email link.
            </Sub>
         </Intro>

         <CodeInput
            value={code}
            onChange={(e) => onChange(e.target.value)}
            placeholder="XXXX-XXXX"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            maxLength={9}
            onKeyDown={(e) => {
               if (e.key === 'Enter') handleSubmit();
            }}
         />

         <Hint>
            No I, O, 0, 1, or L. Codes expire 7 days after the seller sent
            them.
         </Hint>

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={handleSubmit}>
               <Btn
                  title={working ? 'Looking up…' : 'Find transfer →'}
                  type={working ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={working ? colors.GRAY10 : colors.PRIMARY}
                  textcolor={working ? colors.GRAY1 : colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

export default ClaimDevice;

const Wrapper = styled.div`
   width: 440px;
   max-width: 100%;
   padding: 4px 0 0;
`;

const Intro = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   gap: 6px;
   margin-bottom: 18px;
`;

const Icon = styled.div`
   font-size: 32px;
   line-height: 1;
   margin-bottom: 2px;
`;

const Heading = styled.h3`
   margin: 0;
   font-size: 16px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const Sub = styled.p`
   margin: 0;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY3};
   max-width: 360px;
`;

const CodeInput = styled.input`
   width: 100%;
   padding: 16px 12px;
   border-radius: 10px;
   border: 2px solid ${colors.PRIMARY}40;
   background: ${colors.PRIMARY}05;
   text-align: center;
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 24px;
   font-weight: 700;
   letter-spacing: 0.18em;
   color: ${colors.PRIMARY};
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
      background: ${colors.PRIMARY}10;
   }
   &::placeholder {
      color: ${colors.GRAY8};
      letter-spacing: 0.18em;
   }
`;

const Hint = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   text-align: center;
   margin-top: 10px;
   margin-bottom: 18px;
   line-height: 1.5;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
